/**
 * One word on screen, with a reason.
 *
 * Everything the branch has promised and not yet delivered is **reserved**, in
 * one of two shapes: a **slot** (a position on a named clinician inside a window
 * it may move within) or a **load** (points per discipline per week, with no
 * position). The screens never say which shape a thing is; they say Reserved and
 * name the reason.
 *
 * Four reasons, and no fifth without the script saying so:
 *
 *   held for SOC   — a start-of-care hold, a slot on the clinician's own day.
 *   pending auth   — a plan visit beyond the authorization, a slot with a day.
 *   awaiting plan  — a load: the tail of an accepted referral, or a discipline
 *                    that was ordered before its frequencies landed.
 *   recert window  — the recertification assessment, a slot inside days 56–60.
 *
 * The heat map's reserved layer carries the first three. The recert counts in
 * **Planned** because it is inside the authorization, so "recert window" is a
 * card label rather than a layer: moving it between layers to make the hover
 * read prettier would move `committedPoints`, which is a number the referral
 * panel and Act 5 both quote.
 */

export type ReservedReason = "heldForSoc" | "pendingAuth" | "awaitingPlan" | "recertWindow" | "planned";

/** Lower case: these are read inside a sentence ("Reserved · held for SOC"). */
export const RESERVED_REASON_LABEL: Record<ReservedReason, string> = {
  heldForSoc: "held for SOC",
  pendingAuth: "pending auth",
  awaitingPlan: "awaiting plan",
  recertWindow: "recert window",
  // A placeholder the plan put on a day and nothing is gating but scheduling.
  // The commonest hatched thing there is, and the calendar needs a word for it.
  planned: "planned",
};

/** The whole label a hatched card or chip carries. */
export function reservedLabel(reason: ReservedReason): string {
  return `Reserved · ${RESERVED_REASON_LABEL[reason]}`;
}

/** The reasons that carry points on the branch read. `recertWindow` is not one: see above. */
export type LayerReason = Extract<ReservedReason, "heldForSoc" | "pendingAuth" | "awaitingPlan">;

export const LAYER_REASONS: LayerReason[] = ["heldForSoc", "pendingAuth", "awaitingPlan"];

export type ReservedByReason = Record<LayerReason, number>;

export function emptyReserved(): ReservedByReason {
  return { heldForSoc: 0, pendingAuth: 0, awaitingPlan: 0 };
}

export function addReserved(into: ReservedByReason, from: ReservedByReason): void {
  for (const reason of LAYER_REASONS) into[reason] += from[reason];
}

export function reservedTotal(reserved: ReservedByReason): number {
  return Math.round(LAYER_REASONS.reduce((sum, reason) => sum + reserved[reason], 0) * 4) / 4;
}

export function roundReserved(reserved: ReservedByReason): ReservedByReason {
  const out = emptyReserved();
  for (const reason of LAYER_REASONS) out[reason] = Math.round(reserved[reason] * 4) / 4;
  return out;
}

/**
 * The hover's breakdown: every reason, in the script's order, including the ones
 * reading zero. A breakdown that drops its empty parts reads differently from
 * week to week, and the beat is that the same three names are always there.
 */
export function describeReserved(reserved: ReservedByReason): string {
  return LAYER_REASONS.map((reason) => `${RESERVED_REASON_LABEL[reason]} ${Math.round(reserved[reason] * 4) / 4}`).join(" · ");
}
